/**
 * Admin: ATC Control Panel — tune commission/penalty/urgent-pin rates + view total AT locked in escrow.
 * Route: /admin/atc  ·  Backend: /api/admin/atc/{settings,stats}
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';
import Sparkline from '../../components/Sparkline';

const FIELDS = [
  { key: 'b2c_commission_pct',    label: 'B2C Commission %',        step: 0.5, min: 0, max: 30 },
  { key: 'b2b_commission_pct',    label: 'B2B Commission %',        step: 0.5, min: 0, max: 30 },
  { key: 'anti_spam_hold_pct',    label: 'Anti-Spam Hold %',        step: 1,   min: 0, max: 100 },
  { key: 'cancel_penalty_pct',    label: 'Cancel Penalty %',        step: 0.5, min: 0, max: 20 },
  { key: 'urgent_pin_cost_at',    label: 'Urgent-Pin Cost (AT)',    step: 5,   min: 0, max: 1000 },
  { key: 'urgent_pin_hours',      label: 'Urgent-Pin Duration (h)', step: 1,   min: 1, max: 48 },
  { key: 'min_sponsors_for_l2_l3',label: 'Min Sponsors for L2/L3',  step: 1,   min: 0, max: 20 },
  { key: 'b2b_min_moq',           label: 'B2B Min MOQ (pcs)',       step: 5,   min: 1, max: 5000 },
];

export default function AdminATC() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [saleDisputes, setSaleDisputes] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [heatmapDays, setHeatmapDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, st, an, dp, sdp, hm] = await Promise.all([
        api.get('/atc/settings'),
        api.get('/admin/atc/stats'),
        api.get('/admin/atc/analytics'),
        api.get('/admin/atc/disputes', { params: { status: 'OPEN' } }),
        api.get('/admin/atc-seller/sale-disputes', { params: { status: 'OPEN' } }),
        api.get('/admin/atc/heatmap', { params: { days: heatmapDays } }),
      ]);
      setSettings(s.data); setStats(st.data); setAnalytics(an.data);
      setDisputes(dp.data.disputes || []);
      setSaleDisputes(sdp.data.disputes || []);
      setHeatmap(hm.data);
    } catch (e) { toast.error('Failed to load ATC panel'); }
  }, [heatmapDays]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      const patch = {};
      FIELDS.forEach((f) => { patch[f.key] = Number(settings[f.key]); });
      const r = await api.post('/admin/atc/settings', patch);
      setSettings(r.data.settings);
      toast.success('✅ ATC settings updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (!settings || !stats) {
    return <div className="p-6 text-white">⏳ Loading ATC panel…</div>;
  }

  return (
    <div className="min-h-screen pt-6 pb-16 px-4 bg-slate-950 text-white" data-testid="admin-atc-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-2xl font-black mb-1">🏬 ATC Control Panel</div>
        <div className="text-xs opacity-70 mb-4">Anant Trade Center · commission engine, penalties, urgent-pin, and live escrow snapshot.</div>

        {/* Wave 46 · Quick link to full Commission Ledger */}
        <a href="/admin/commission-ledger" data-testid="atc-open-commission-ledger"
           className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black hover:bg-emerald-400">
          💰 Open Full Commission Ledger →
        </a>

        {/* Live stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <StatCard label="Open demands"     value={stats.demands_open} color="emerald" testid="atc-stat-open" />
          <StatCard label="Escrow orders"    value={stats.orders_escrow} color="amber"   testid="atc-stat-escrow" />
          <StatCard label="Completed orders" value={stats.orders_completed} color="cyan"  testid="atc-stat-completed" />
          <StatCard label="Total demands"    value={stats.demands_total} color="purple" testid="atc-stat-total" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
          <StatCard label="AT locked · commitments" value={`${stats.at_locked_commitment} AT`} color="cyan"    testid="atc-stat-commit" wide />
          <StatCard label="AT locked · escrow"      value={`${stats.at_locked_escrow} AT`}      color="amber"  testid="atc-stat-lock"   wide />
          <StatCard label="AT locked · TOTAL"        value={`${stats.at_locked_total} AT`}       color="rose"   testid="atc-stat-locked-total" wide />
        </div>

        <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/40 p-2 mb-4 text-xs">
          <b>💰 Commission collected all-time:</b> {stats.commission_collected_at} AT
        </div>

        {/* Wave 37 · Dispute SLA Alerts banner (>12h unresolved) */}
        <SlaAlertsBanner />

        {/* Dispute Queue */}
        <DisputeQueue disputes={disputes} onResolved={load} />

        {/* Wave 45 · Sale-Post Dispute Queue (buyer-raised on completed sale) */}
        <SaleDisputeQueue disputes={saleDisputes} onResolved={load} />

        {/* Wave 36 · Appointment Disputes Queue (buyer reported shop no-show) */}
        <AppointmentDisputesQueue />

        {/* Wave 46 · Soundbox global defaults */}
        <SoundboxDefaultsPanel />

        {/* Buyer-Intent Heatmap (Wave 25) */}
        {heatmap && (
          <HeatmapPanel
            heatmap={heatmap}
            days={heatmapDays}
            onChangeDays={(d) => setHeatmapDays(d)}
          />
        )}

        {/* Wave 30 · Live Signals + Full Deal Control */}
        <AdminSignalsPanel />
        <AdminDealControlPanel />

        {/* Analytics — top cities, categories, undercutters */}
        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5" data-testid="admin-atc-analytics">
            <AnalyticsCard title="📍 Top Cities" testid="atc-top-cities" empty="No cities yet"
                           rows={analytics.top_cities}
                           render={(c) => (
                             <>
                               <span className="capitalize">{c.city}</span>
                               <span className="ml-auto text-[10px] opacity-70">{c.demands} demands · {c.budget_total_at} AT</span>
                             </>
                           )} />
            <AnalyticsCard title="🏷 Hottest Categories" testid="atc-top-cats" empty="No categories yet"
                           rows={analytics.top_categories}
                           render={(c) => (
                             <>
                               <span className="capitalize">{c.category}</span>
                               <span className="ml-auto text-[10px] opacity-70">{c.demands} demands · avg {c.avg_budget_at} AT</span>
                             </>
                           )} />
            <AnalyticsCard title="💥 Biggest Undercutters" testid="atc-top-undercutters" empty="No undercutters yet"
                           rows={analytics.top_undercutters}
                           render={(u) => (
                             <>
                               <span>@{u.username || u.merchant_id?.slice(0, 8)}</span>
                               <span className="ml-auto text-[10px] opacity-70">{u.lowest_bids} 🥇 · {u.wins} wins</span>
                             </>
                           )} />
          </div>
        )}

        {/* Settings form */}
        <div className="rounded-2xl bg-slate-900 border border-slate-700 p-4 space-y-2" data-testid="admin-atc-settings">
          <div className="text-sm font-black text-purple-300 mb-2">⚙️ Rate Configuration</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <label key={f.key} className="flex flex-col gap-1">
                <span className="text-[10px] font-black text-slate-300 uppercase">{f.label}</span>
                <input type="number" step={f.step} min={f.min} max={f.max}
                       value={settings[f.key] ?? 0}
                       data-testid={`atc-input-${f.key}`}
                       onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                       className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
              </label>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={load} className="px-3 py-2 rounded-xl bg-slate-700 text-xs font-black">↻ Reload</button>
            <button onClick={async () => {
              try {
                const r = await api.get('/admin/atc/cashflow-csv', { params: { days: 30 }, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement('a');
                a.href = url; a.download = `atc_cashflow_30d.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) { toast.error('CSV download failed'); }
            }} data-testid="admin-atc-cashflow-csv"
               className="px-3 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-black">
              ⬇️ Cashflow CSV (30d)
            </button>
            <button onClick={save} disabled={busy} data-testid="admin-atc-save"
                    className="ml-auto px-5 py-2 rounded-xl bg-emerald-500 text-slate-900 text-sm font-black disabled:opacity-50">
              {busy ? '⏳' : '💾 Save Settings'}
            </button>
          </div>
        </div>

        <div className="mt-4 text-[10px] opacity-60">
          Section-7 splits are fixed per spec: L1 10% · L2 7% · L3 5% · Govt Tax 10% · Admin Net = remainder.
          Adjust base commission % (above) to move the whole pool.
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color = 'emerald', testid, wide }) {
  const cls = {
    emerald: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-200',
    amber:   'bg-amber-500/15 border-amber-400/40 text-amber-200',
    cyan:    'bg-cyan-500/15 border-cyan-400/40 text-cyan-200',
    purple:  'bg-purple-500/15 border-purple-400/40 text-purple-200',
    rose:    'bg-rose-500/15 border-rose-400/40 text-rose-200',
  }[color];
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={testid}>
      <div className="text-[10px] font-black uppercase opacity-80">{label}</div>
      <div className={`font-black mt-0.5 ${wide ? 'text-lg' : 'text-2xl'}`}>{value}</div>
    </div>
  );
}

function AnalyticsCard({ title, rows, render, testid, empty }) {
  return (
    <div className="rounded-xl bg-slate-900 border border-slate-700 p-3" data-testid={testid}>
      <div className="text-xs font-black text-purple-300 mb-2">{title}</div>
      {(!rows || rows.length === 0) && <div className="text-[10px] opacity-60 py-2">{empty}</div>}
      <div className="space-y-1">
        {(rows || []).map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-slate-800/60">
            <span className="text-[10px] font-black text-slate-400 w-4">{i + 1}.</span>
            {render(r)}
          </div>
        ))}
      </div>
    </div>
  );
}

function DisputeQueue({ disputes, onResolved }) {
  const [busyId, setBusyId] = useState(null);
  const [noteMap, setNoteMap] = useState({});

  const resolve = async (id, action) => {
    if (!window.confirm(`Confirm action: ${action}?`)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/atc/disputes/${id}/resolve`, { action, admin_note: noteMap[id] || null });
      toast.success(`✅ Dispute ${action}`);
      onResolved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-rose-400/40 p-3 mb-4" data-testid="admin-atc-disputes">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-black text-rose-300">🚨 Dispute Queue</div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500 text-white font-black">{disputes.length} OPEN</span>
      </div>
      {disputes.length === 0 && <div className="text-[11px] opacity-60 py-2 text-center">No open disputes 🎉</div>}
      <div className="space-y-2">
        {disputes.map((d) => (
          <div key={d.id} className="rounded-lg bg-slate-800 border border-slate-700 p-2 space-y-1.5" data-testid={`atc-dispute-${d.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap text-[11px]">
              <div className="min-w-0">
                <span className="font-black text-rose-300">₹{d.amount_at} AT</span>
                <span className="opacity-80 ml-1">· {d.deal_type}</span>
              </div>
              <div className="text-[10px] opacity-70">Order <b>{d.order_id.slice(0, 8)}</b> · @{d.buyer_username} vs @{d.seller_username}</div>
            </div>
            <div className="text-xs opacity-90"><b>Reason:</b> {d.reason}</div>
            <input value={noteMap[d.id] || ''} onChange={(e) => setNoteMap({ ...noteMap, [d.id]: e.target.value })}
                   placeholder="Admin note (optional)" data-testid={`atc-dispute-note-${d.id}`}
                   className="w-full px-2 py-1 rounded bg-slate-900 border border-slate-700 text-[11px]" />
            <div className="flex gap-1.5">
              <button onClick={() => resolve(d.id, 'refund_buyer')} disabled={busyId === d.id}
                      data-testid={`atc-dispute-refund-${d.id}`}
                      className="flex-1 px-2 py-1.5 rounded bg-rose-500 text-white text-[10px] font-black disabled:opacity-50">💸 Refund Buyer</button>
              <button onClick={() => resolve(d.id, 'release_seller')} disabled={busyId === d.id}
                      data-testid={`atc-dispute-release-${d.id}`}
                      className="flex-1 px-2 py-1.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black disabled:opacity-50">✅ Release Seller</button>
              <button onClick={() => resolve(d.id, 'dismiss')} disabled={busyId === d.id}
                      data-testid={`atc-dispute-dismiss-${d.id}`}
                      className="flex-1 px-2 py-1.5 rounded bg-slate-700 text-slate-200 text-[10px] font-black disabled:opacity-50">🗑 Dismiss</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============ Wave 45 · Sale-Post Dispute Queue (mandatory admin note) ============
function SaleDisputeQueue({ disputes, onResolved }) {
  const [busyId, setBusyId] = useState(null);
  const [noteMap, setNoteMap] = useState({});

  const resolve = async (id, action) => {
    const note = (noteMap[id] || '').trim();
    if (note.length < 5) {
      toast.error('Admin note is mandatory (min 5 chars) for audit trail');
      return;
    }
    const labelMap = { refund_buyer: '💸 Refund Buyer', release_seller: '✅ Uphold Sale', dismiss: '🗑 Dismiss' };
    if (!window.confirm(`${labelMap[action]}?\n\nNote: ${note}`)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/atc-seller/sale-disputes/${id}/resolve`, { action, admin_note: note });
      toast.success(`✅ Sale dispute ${action}`);
      onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resolve failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-fuchsia-400/40 p-3 mb-4" data-testid="admin-atc-sale-disputes">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-black text-fuchsia-300">🛒 Sale-Post Dispute Queue</div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500 text-white font-black">{disputes.length} OPEN</span>
      </div>
      {disputes.length === 0 && <div className="text-[11px] opacity-60 py-2 text-center">No sale disputes 🎉</div>}
      <div className="space-y-2">
        {disputes.map((d) => (
          <div key={d.id} className="rounded-lg bg-slate-800 border border-slate-700 p-2 space-y-1.5" data-testid={`atc-sale-dispute-${d.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap text-[11px]">
              <div className="min-w-0 flex items-center gap-2">
                {d.post?.images?.[0] && (
                  <img src={d.post.images[0]} alt="" className="w-9 h-9 rounded object-cover border border-slate-700" />
                )}
                <div className="min-w-0">
                  <div className="font-black text-fuchsia-200 truncate max-w-[180px]">{d.post?.title || 'Sale post'}</div>
                  <div className="text-[9px] opacity-70">🏷️ {d.post?.category || '—'} · {d.post?.condition || '—'}</div>
                </div>
              </div>
              <div className="text-[10px] opacity-70 text-right">
                <div className="font-black text-fuchsia-300">₹{d.amount_at} AT</div>
                <div>@{d.buyer_username} vs @{d.seller_username}</div>
              </div>
            </div>
            <div className="text-xs opacity-90"><b>Reason:</b> {d.reason}</div>
            {d.description && <div className="text-[11px] opacity-80 italic">"{d.description}"</div>}
            <input value={noteMap[d.id] || ''} onChange={(e) => setNoteMap({ ...noteMap, [d.id]: e.target.value })}
                   placeholder="Admin note (MANDATORY · min 5 chars · audit trail)"
                   data-testid={`atc-sale-dispute-note-${d.id}`}
                   className="w-full px-2 py-1 rounded bg-slate-900 border border-fuchsia-500/40 text-[11px] placeholder-fuchsia-300/40" />
            <div className="flex gap-1.5">
              <button onClick={() => resolve(d.id, 'refund_buyer')} disabled={busyId === d.id}
                      data-testid={`atc-sale-dispute-refund-${d.id}`}
                      className="flex-1 px-2 py-1.5 rounded bg-rose-500 text-white text-[10px] font-black disabled:opacity-50">💸 Refund Buyer</button>
              <button onClick={() => resolve(d.id, 'release_seller')} disabled={busyId === d.id}
                      data-testid={`atc-sale-dispute-release-${d.id}`}
                      className="flex-1 px-2 py-1.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black disabled:opacity-50">✅ Uphold Sale</button>
              <button onClick={() => resolve(d.id, 'dismiss')} disabled={busyId === d.id}
                      data-testid={`atc-sale-dispute-dismiss-${d.id}`}
                      className="flex-1 px-2 py-1.5 rounded bg-slate-700 text-slate-200 text-[10px] font-black disabled:opacity-50">🗑 Dismiss</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============ Wave 46 · Soundbox Global Defaults (admin) ============
function SoundboxDefaultsPanel() {
  const [defaults, setDefaults] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/soundbox/defaults').then((r) => setDefaults(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!defaults) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/soundbox/defaults', {
        default_language: defaults.default_language || 'hi-IN',
        default_rate: Number(defaults.default_rate) || 1.0,
        default_voice_name: defaults.default_voice_name || null,
        mute_default: !!defaults.mute_default,
      });
      setDefaults(r.data.defaults || defaults);
      toast.success('✅ Soundbox defaults updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (!defaults) return null;
  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-cyan-400/40 p-3 mb-4" data-testid="admin-soundbox-defaults">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-sm font-black text-cyan-300">🔊 Soundbox Global Defaults</div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200">browser-only · 0 cost</span>
      </div>
      <div className="text-[10px] opacity-70 mb-2">First-time users inherit these settings; users can override locally.</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <div className="text-[10px] opacity-70 mb-0.5">Language</div>
          <select value={defaults.default_language || 'hi-IN'}
                  onChange={(e) => setDefaults({ ...defaults, default_language: e.target.value })}
                  data-testid="admin-soundbox-lang"
                  className="w-full px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-[11px]">
            {['hi-IN', 'en-IN', 'en-US', 'mr-IN', 'gu-IN', 'ta-IN', 'pa-IN', 'bn-IN'].map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-[10px] opacity-70 mb-0.5">Speed</div>
          <select value={defaults.default_rate || 1.0}
                  onChange={(e) => setDefaults({ ...defaults, default_rate: Number(e.target.value) })}
                  data-testid="admin-soundbox-rate"
                  className="w-full px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-[11px]">
            <option value={0.7}>🐢 Slow (0.7×)</option>
            <option value={1.0}>🚶 Medium (1.0×)</option>
            <option value={1.5}>🚀 Fast (1.5×)</option>
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-[11px] font-black">
            <input type="checkbox" checked={!!defaults.mute_default}
                   onChange={(e) => setDefaults({ ...defaults, mute_default: e.target.checked })}
                   data-testid="admin-soundbox-mute-default" />
            <span>Mute by default</span>
          </label>
        </div>
      </div>
      <button onClick={save} disabled={busy} data-testid="admin-soundbox-save"
              className="mt-2 px-3 py-1.5 rounded-lg bg-cyan-500 text-slate-900 text-[11px] font-black disabled:opacity-50">
        💾 Save Defaults
      </button>
    </div>
  );
}


// ============ Wave 25: Buyer Intent Heatmap ============
function HeatmapPanel({ heatmap, days, onChangeDays }) {
  const { cities = [], categories = [], cells = [] } = heatmap || {};
  const [drill, setDrill] = useState(null); // { city, category }
  // Build a lookup map: `${city}|${category}` → { demands, budget_total_at }
  const map = {};
  let maxDemands = 0;
  cells.forEach((c) => {
    const key = `${c.city}|${c.category}`;
    map[key] = c;
    if (c.demands > maxDemands) maxDemands = c.demands;
  });

  const totalDemands = cells.reduce((a, c) => a + c.demands, 0);
  const totalBudget  = cells.reduce((a, c) => a + (c.budget_total_at || 0), 0);

  const intensity = (n) => {
    if (!n || !maxDemands) return { bg: 'rgba(15,23,42,0.6)', fg: 'rgba(148,163,184,0.6)' };
    // 0 → 1
    const p = Math.min(1, n / maxDemands);
    // Emerald → Amber → Rose gradient
    const hue = 160 - p * 160;       // 160 (emerald) → 0 (rose)
    const sat = 75;
    const light = 55 - p * 15;       // 55% → 40%
    const alpha = 0.25 + p * 0.65;   // 0.25 → 0.90
    return {
      bg: `hsla(${hue}, ${sat}%, ${light}%, ${alpha})`,
      fg: p > 0.5 ? '#0f172a' : '#f1f5f9',
    };
  };

  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-cyan-400/40 p-3 mb-4" data-testid="admin-atc-heatmap">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div>
          <div className="text-sm font-black text-cyan-300">🔥 Buyer-Intent Heatmap</div>
          <div className="text-[10px] opacity-70">City × Category demand density · last {days} days</div>
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => onChangeDays(d)}
                    data-testid={`atc-heatmap-days-${d}`}
                    className={`px-2 py-1 rounded text-[10px] font-black ${days === d ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 text-[10px] mb-2 flex-wrap">
        <span className="opacity-70">📊 Total demands: <b className="text-cyan-300">{totalDemands}</b></span>
        <span className="opacity-70">💰 Budget pool: <b className="text-emerald-300">{Math.round(totalBudget)} AT</b></span>
        <span className="opacity-70">🏙 {cities.length} cities · 🏷 {categories.length} categories</span>
      </div>

      {cells.length === 0 && (
        <div className="text-[11px] opacity-60 py-4 text-center">No demand data in the last {days} days.</div>
      )}

      {cells.length > 0 && (
        <>
          {/* Legend */}
          <div className="flex items-center gap-1 mb-2 text-[9px] opacity-80">
            <span>Low</span>
            <span className="w-4 h-3 rounded" style={{ background: intensity(0.05 * maxDemands).bg }} />
            <span className="w-4 h-3 rounded" style={{ background: intensity(0.35 * maxDemands).bg }} />
            <span className="w-4 h-3 rounded" style={{ background: intensity(0.65 * maxDemands).bg }} />
            <span className="w-4 h-3 rounded" style={{ background: intensity(maxDemands).bg }} />
            <span>High · Peak {maxDemands}</span>
          </div>

          <div className="overflow-x-auto" data-testid="atc-heatmap-grid">
            <table className="w-full border-collapse text-[10px]">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-900 text-left px-2 py-1 border-b border-slate-700 text-slate-400 font-black">City ↓ / Category →</th>
                  {categories.map((c) => (
                    <th key={c} className="px-2 py-1 border-b border-slate-700 text-slate-300 font-black capitalize whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cities.map((city) => (
                  <tr key={city}>
                    <td className="sticky left-0 z-10 bg-slate-900 px-2 py-1 border-b border-slate-800 capitalize font-black text-slate-300 whitespace-nowrap">{city}</td>
                    {categories.map((cat) => {
                      const cell = map[`${city}|${cat}`];
                      const n = cell?.demands || 0;
                      const { bg, fg } = intensity(n);
                      return (
                        <td key={cat}
                            onClick={() => n > 0 && setDrill({ city, category: cat })}
                            title={cell ? `${city} · ${cat}\n${n} demands · ${Math.round(cell.budget_total_at)} AT · Click for details` : `${city} · ${cat}\nNo data`}
                            data-testid={`atc-heatmap-${city}-${cat}`}
                            className={`px-2 py-1.5 border border-slate-800 text-center font-black ${n > 0 ? 'cursor-pointer hover:scale-105 transition' : ''}`}
                            style={{ background: bg, color: fg }}>
                          {n || ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {drill && <HeatmapDrillModal city={drill.city} category={drill.category} days={days} onClose={() => setDrill(null)} />}
    </div>
  );
}

function HeatmapDrillModal({ city, category, days, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get('/admin/atc/heatmap/cell', { params: { city, category, days } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Load failed'));
  }, [city, category, days]);
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="atc-heatmap-drill-modal"
           className="w-full max-w-2xl rounded-2xl bg-slate-900 border-2 border-cyan-400/60 p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="text-lg font-black text-cyan-300">🔥 {city.charAt(0).toUpperCase() + city.slice(1)} × {category.charAt(0).toUpperCase() + category.slice(1)}</div>
            <div className="text-[10px] opacity-70">Last {days} days · demand drill-down</div>
          </div>
          <button onClick={onClose} data-testid="atc-heatmap-drill-close" className="px-2 py-0.5 rounded bg-slate-700 text-xs">✕</button>
        </div>
        {err && <div className="text-rose-300 text-xs">{err}</div>}
        {!data && !err && <div className="text-[11px] opacity-70 py-8 text-center">⏳ Loading…</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <MiniStat label="Total demands" value={data.total_demands} accent="cyan" />
              <MiniStat label="Budget pool"   value={`${Math.round(data.total_budget_at)} AT`} accent="emerald" />
              <MiniStat label="Completed"     value={data.completed_count} accent="amber" />
              <MiniStat label="Avg close time" value={`${data.avg_close_hours}h`} accent="purple" />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mb-3 text-[10px]">
              {Object.entries(data.status_counts || {}).map(([s, n]) => (
                <span key={s} className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 font-black">
                  {s}: <b className="text-cyan-300">{n}</b>
                </span>
              ))}
            </div>
            <div className="text-[11px] font-black opacity-80 mb-1">📋 Demand list</div>
            <div className="space-y-1.5" data-testid="atc-heatmap-drill-list">
              {data.demands.length === 0 && <div className="text-[11px] opacity-60 py-4 text-center">No demands.</div>}
              {data.demands.map((d) => (
                <div key={d.id} className="rounded-lg bg-slate-800/70 border border-slate-700 p-2 text-[11px]" data-testid={`atc-drill-demand-${d.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-black flex-1 min-w-0 truncate">{d.title}</div>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                      d.status === 'COMPLETED' ? 'bg-emerald-500/25 text-emerald-300'
                      : d.status === 'LOCKED' ? 'bg-amber-500/25 text-amber-300'
                      : d.status === 'CANCELLED' ? 'bg-rose-500/25 text-rose-300'
                      : 'bg-slate-700 text-slate-200'
                    }`}>{d.status}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[10px] opacity-80">
                    <span>@{d.buyer_username}</span>
                    <span>· 💰 {d.target_budget_at} AT</span>
                    <span>· 📦 {d.quantity}</span>
                    <span>· 🔥 {d.bid_count || 0} bids</span>
                    <span className="ml-auto opacity-60">{d.deal_type === 'B2B_BULK' ? 'B2B' : 'B2C'}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  const cls = {
    cyan: 'text-cyan-300 border-cyan-400/40 bg-cyan-500/10',
    emerald: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10',
    amber: 'text-amber-300 border-amber-400/40 bg-amber-500/10',
    purple: 'text-purple-300 border-purple-400/40 bg-purple-500/10',
  }[accent] || 'text-white border-slate-700 bg-slate-800';
  return (
    <div className={`rounded-lg border p-2 ${cls}`}>
      <div className="text-[9px] font-black opacity-80 uppercase">{label}</div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
}

// =============== Wave 30 · Admin Live Signals Feed (SSE) ===============
function AdminSignalsPanel() {
  const [signals, setSignals] = useState([]);
  const [connected, setConnected] = useState(false);
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    if (!backend) return;
    const es = new EventSource(`${backend}/api/atc/broadcast/stream`);
    const push = (kind, emoji, text, extra = {}) => {
      setSignals((prev) => [{ id: 'sig-' + Date.now() + Math.random(), kind, emoji, text, ts: Date.now(), ...extra }, ...prev].slice(0, 40));
      setPulse((p) => p + 1);
    };
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.addEventListener('demand_created', (e) => {
      try {
        const d = JSON.parse(e.data);
        push('demand', d.urgent ? '⚡' : '📢',
          `New ${d.deal_type === 'B2B_BULK' ? 'B2B' : 'B2C'} demand · ${d.title}${d.city ? ' · ' + d.city : ''} · budget ${d.budget} AT`);
      } catch {}
    });
    es.addEventListener('bid_placed', (e) => {
      try {
        const d = JSON.parse(e.data);
        push('bid', d.is_lowest ? '💥' : '💬',
          d.is_lowest ? `UNDERCUT · @${d.merchant} bid ${d.offered_price} AT` : `@${d.merchant} bid ${d.offered_price} AT`);
      } catch {}
    });
    es.addEventListener('deal_locked', (e) => {
      try {
        const d = JSON.parse(e.data);
        push('lock', '🎉',
          `Deal locked · @${d.seller} → @${d.buyer} · ${d.amount} AT${d.admin ? ' (admin)' : d.early ? ' (early-complete)' : ''}`);
      } catch {}
    });
    es.addEventListener('voice_reply', (e) => {
      try {
        const d = JSON.parse(e.data);
        push('voice', '💬', `Voice reply · @${d.from_username} (${d.role})${d.note ? ' — ' + d.note : ''}`);
      } catch {}
    });
    return () => es.close();
  }, []);

  const rel = (ts) => {
    const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); return `${h}h`;
  };

  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-emerald-400/40 p-3 mb-4" data-testid="admin-atc-signals">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div>
          <div className="text-sm font-black text-emerald-300 flex items-center gap-2">
            📡 Live Signals · SSE
            <span key={pulse} className={`inline-block w-2 h-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-rose-400'} ${connected ? 'animate-pulse' : ''}`} />
            <span className="text-[10px] opacity-70">{connected ? 'connected' : 'reconnecting…'}</span>
          </div>
          <div className="text-[10px] opacity-70">Real-time demands · bids · deal-locks · voice replies</div>
        </div>
        <button onClick={() => setSignals([])} data-testid="admin-signals-clear"
                className="text-[10px] px-2 py-1 rounded bg-slate-800 border border-slate-700 hover:bg-slate-700">Clear</button>
      </div>
      <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
        {signals.length === 0 && <div className="text-[11px] opacity-60 py-6 text-center">Waiting for signals…</div>}
        {signals.map((s) => (
          <div key={s.id} data-testid={`admin-signal-${s.kind}`}
               className={`flex items-center gap-2 p-2 rounded-lg border text-[11px] ${
                 s.kind === 'lock' ? 'bg-emerald-500/10 border-emerald-400/30'
                 : s.kind === 'bid' ? 'bg-cyan-500/10 border-cyan-400/30'
                 : s.kind === 'demand' ? 'bg-amber-500/10 border-amber-400/30'
                 : 'bg-slate-800 border-slate-700'
               }`}>
            <span className="text-base">{s.emoji}</span>
            <span className="flex-1 min-w-0">{s.text}</span>
            <span className="text-[9px] opacity-60">{rel(s.ts)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// =============== Wave 30 · Admin Full Deal Control ===============
function AdminDealControlPanel() {
  const [tab, setTab] = useState('orders'); // 'orders' | 'demands' | 'auto'
  const [orders, setOrders] = useState([]);
  const [demands, setDemands] = useState([]);
  const [status, setStatus] = useState('ESCROW_LOCKED');
  const [dealType, setDealType] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(new Set()); // multi-select order_ids
  const [autoRules, setAutoRules] = useState({ enabled: false, hours: 48, skip_disputed: true });
  const [autoBusy, setAutoBusy] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      const r = await api.get('/admin/atc/orders', { params: { status: status || undefined, limit: 60 } });
      setOrders(r.data.orders || []);
      setSelected(new Set());
    } catch (e) { toast.error('Failed to load orders'); }
  }, [status]);
  const loadDemands = useCallback(async () => {
    try {
      const r = await api.get('/admin/atc/demands', {
        params: { status: status || undefined, deal_type: dealType || undefined, limit: 60 } });
      setDemands(r.data.demands || []);
    } catch (e) { toast.error('Failed to load demands'); }
  }, [status, dealType]);
  const loadAutoRules = useCallback(async () => {
    try {
      const r = await api.get('/admin/atc/auto-settle-rules');
      setAutoRules(r.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (tab === 'orders') loadOrders();
    else if (tab === 'demands') loadDemands();
    else if (tab === 'auto') loadAutoRules();
  }, [tab, loadOrders, loadDemands, loadAutoRules]);

  const toggleSel = (oid) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(oid)) n.delete(oid); else n.add(oid);
      return n;
    });
  };
  const selectAllEligible = () => {
    const eligible = orders.filter((o) => ['ESCROW_LOCKED', 'DISPUTED'].includes(o.status)).map((o) => o.order_id);
    setSelected(new Set(eligible));
  };
  const bulkSettle = async () => {
    if (selected.size === 0) return toast.error('Select at least one order');
    if (selected.size > 50) return toast.error('Max 50 per batch');
    if (!window.confirm(`Batch-settle ${selected.size} orders? Escrow releases + commission split all at once.`)) return;
    setAutoBusy(true);
    try {
      const reason = window.prompt('Reason (optional):') || 'bulk_settle';
      const r = await api.post('/admin/atc/orders/bulk-settle', { order_ids: Array.from(selected), reason });
      toast.success(`✅ ${r.data.settled_count} orders settled${r.data.failed?.length ? ` · ${r.data.failed.length} failed` : ''}`);
      loadOrders();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk settle failed'); }
    finally { setAutoBusy(false); }
  };

  const forceComplete = async (o) => {
    if (!window.confirm(`Release ${o.total_amount_at} AT escrow to @${o.seller_username}?\nThis will split commission & credit referral cashback.`)) return;
    setBusyId(o.order_id);
    try {
      const reason = window.prompt('Reason (optional):') || null;
      const r = await api.post(`/admin/atc/order/${o.order_id}/force-complete`, { reason });
      toast.success(`✅ Settled · commission split ${JSON.stringify(r.data.commission_split)}`);
      loadOrders();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Force-complete failed'); }
    finally { setBusyId(null); }
  };
  const forceCancel = async (o) => {
    if (!window.confirm(`Refund full ${o.total_amount_at} AT to @${o.buyer_username}?\nThis cancels the order permanently.`)) return;
    setBusyId(o.order_id);
    try {
      const reason = window.prompt('Refund reason:') || 'admin refund';
      await api.post(`/admin/atc/order/${o.order_id}/force-cancel`, { reason });
      toast.success('✅ Full refund issued');
      loadOrders();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Force-cancel failed'); }
    finally { setBusyId(null); }
  };
  const forceCloseDemand = async (d) => {
    if (!window.confirm(`Close demand "${d.title}"? Any anti-spam hold will be refunded.`)) return;
    setBusyId(d.id);
    try {
      const reason = window.prompt('Reason (optional):') || 'admin close';
      await api.post(`/admin/atc/demand/${d.id}/force-close`, { reason });
      toast.success('✅ Demand closed');
      loadDemands();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Force-close failed'); }
    finally { setBusyId(null); }
  };
  const saveAutoRules = async (patch = {}) => {
    setAutoBusy(true);
    try {
      const next = { ...autoRules, ...patch };
      const r = await api.post('/admin/atc/auto-settle-rules', next);
      setAutoRules({ ...autoRules, ...r.data });
      toast.success('✅ Auto-settle rules saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setAutoBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-purple-400/40 p-3 mb-4" data-testid="admin-atc-control">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="text-sm font-black text-purple-300">🎛️ Full Deal Control</div>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <div className="flex bg-slate-800 rounded-lg p-0.5">
            <button onClick={() => setTab('orders')} data-testid="admin-tab-orders"
                    className={`px-3 py-1 rounded text-[11px] font-black ${tab === 'orders' ? 'bg-purple-500 text-white' : 'text-slate-300'}`}>Orders</button>
            <button onClick={() => setTab('demands')} data-testid="admin-tab-demands"
                    className={`px-3 py-1 rounded text-[11px] font-black ${tab === 'demands' ? 'bg-purple-500 text-white' : 'text-slate-300'}`}>Demands</button>
            <button onClick={() => setTab('auto')} data-testid="admin-tab-auto"
                    className={`px-3 py-1 rounded text-[11px] font-black ${tab === 'auto' ? 'bg-purple-500 text-white' : 'text-slate-300'}`}>⚡ Auto-Rules</button>
          </div>
          {tab !== 'auto' && (
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-filter-status"
                    className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]">
              <option value="">All statuses</option>
              {tab === 'orders'
                ? ['ESCROW_LOCKED', 'COMPLETED', 'CANCELLED', 'DISPUTED'].map((s) => <option key={s} value={s}>{s}</option>)
                : ['OPEN', 'LOCKED', 'COMPLETED', 'CANCELLED'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {tab === 'demands' && (
            <select value={dealType} onChange={(e) => setDealType(e.target.value)} data-testid="admin-filter-dealtype"
                    className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]">
              <option value="">B2C + B2B</option>
              <option value="B2C_RETAIL">B2C</option>
              <option value="B2B_BULK">B2B</option>
            </select>
          )}
        </div>
      </div>

      {tab === 'orders' && (
        <>
          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-purple-500/15 border border-purple-400/40" data-testid="admin-bulk-bar">
              <span className="text-[11px] font-black text-purple-200">🎯 {selected.size} selected</span>
              <button onClick={selectAllEligible} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 border border-slate-700 hover:bg-slate-700">Select all eligible</button>
              <button onClick={() => setSelected(new Set())} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 border border-slate-700 hover:bg-slate-700">Clear</button>
              <button onClick={bulkSettle} disabled={autoBusy}
                      data-testid="admin-bulk-settle-btn"
                      className="ml-auto px-3 py-1 rounded bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 text-[11px] font-black disabled:opacity-50 hover:brightness-110">
                ⚡ Bulk Settle ({selected.size})
              </button>
            </div>
          )}
          <div className="overflow-x-auto" data-testid="admin-orders-table">
            <table className="w-full text-[11px]">
              <thead className="text-[9px] uppercase text-slate-400">
                <tr>
                  <th className="text-center py-1 px-1 w-6">
                    <input type="checkbox" checked={selected.size > 0 && selected.size === orders.filter((o) => ['ESCROW_LOCKED', 'DISPUTED'].includes(o.status)).length}
                           onChange={(e) => e.target.checked ? selectAllEligible() : setSelected(new Set())}
                           data-testid="admin-select-all" />
                  </th>
                  <th className="text-left py-1 px-2">Order</th>
                  <th className="text-left py-1 px-2">Buyer → Seller</th>
                  <th className="text-right py-1 px-2">Amount</th>
                  <th className="text-center py-1 px-2">Status</th>
                  <th className="text-center py-1 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (<tr><td colSpan="6" className="text-center py-6 opacity-60">No orders.</td></tr>)}
                {orders.map((o) => {
                  const eligible = ['ESCROW_LOCKED', 'DISPUTED'].includes(o.status);
                  return (
                    <tr key={o.order_id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`admin-order-row-${o.order_id}`}>
                      <td className="text-center px-1">
                        {eligible && (
                          <input type="checkbox" checked={selected.has(o.order_id)} onChange={() => toggleSel(o.order_id)}
                                 data-testid={`admin-select-${o.order_id}`} />
                        )}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-[10px]">{String(o.order_id).slice(0, 8)}…</td>
                      <td className="py-1.5 px-2">@{o.buyer_username} → @{o.seller_username}</td>
                      <td className="py-1.5 px-2 text-right font-black text-emerald-300">{o.total_amount_at} AT</td>
                      <td className="py-1.5 px-2 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                          o.status === 'COMPLETED' ? 'bg-emerald-500/25 text-emerald-300'
                          : o.status === 'ESCROW_LOCKED' ? 'bg-amber-500/25 text-amber-300'
                          : o.status === 'DISPUTED' ? 'bg-rose-500/25 text-rose-300'
                          : 'bg-slate-700 text-slate-300'
                        }`}>{o.status}</span>
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        {eligible ? (
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => forceComplete(o)} disabled={busyId === o.order_id}
                                    data-testid={`admin-force-complete-${o.order_id}`}
                                    title="Release full escrow to seller (1-click settle)"
                                    className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black disabled:opacity-50 hover:brightness-110">
                              ✅ Settle
                            </button>
                            <button onClick={() => forceCancel(o)} disabled={busyId === o.order_id}
                                    data-testid={`admin-force-cancel-${o.order_id}`}
                                    title="Refund full amount to buyer"
                                    className="px-2 py-0.5 rounded bg-rose-500 text-white text-[10px] font-black disabled:opacity-50 hover:brightness-110">
                              ↩ Refund
                            </button>
                          </div>
                        ) : (
                          <span className="text-[9px] opacity-50">–</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'demands' && (
        <div className="overflow-x-auto" data-testid="admin-demands-table">
          <table className="w-full text-[11px]">
            <thead className="text-[9px] uppercase text-slate-400">
              <tr>
                <th className="text-left py-1 px-2">Title</th>
                <th className="text-left py-1 px-2">Buyer</th>
                <th className="text-center py-1 px-2">Type</th>
                <th className="text-right py-1 px-2">Budget</th>
                <th className="text-center py-1 px-2">Bids</th>
                <th className="text-center py-1 px-2">Status</th>
                <th className="text-center py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {demands.length === 0 && (<tr><td colSpan="7" className="text-center py-6 opacity-60">No demands.</td></tr>)}
              {demands.map((d) => (
                <tr key={d.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`admin-demand-row-${d.id}`}>
                  <td className="py-1.5 px-2 font-black truncate max-w-[180px]">{d.title}</td>
                  <td className="py-1.5 px-2">@{d.buyer_username}</td>
                  <td className="py-1.5 px-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${d.deal_type === 'B2B_BULK' ? 'bg-amber-500/25 text-amber-300' : 'bg-cyan-500/25 text-cyan-300'}`}>
                      {d.deal_type === 'B2B_BULK' ? 'B2B' : 'B2C'}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-right">{d.target_budget_at} AT</td>
                  <td className="py-1.5 px-2 text-center">🔥 {d.bid_count || 0}</td>
                  <td className="py-1.5 px-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                      d.status === 'OPEN' ? 'bg-emerald-500/25 text-emerald-300'
                      : d.status === 'LOCKED' ? 'bg-amber-500/25 text-amber-300'
                      : d.status === 'COMPLETED' ? 'bg-cyan-500/25 text-cyan-300'
                      : 'bg-slate-700 text-slate-300'
                    }`}>{d.status}</span>
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    {d.status === 'OPEN' ? (
                      <button onClick={() => forceCloseDemand(d)} disabled={busyId === d.id}
                              data-testid={`admin-force-close-${d.id}`}
                              title="Force-close demand + refund anti-spam hold"
                              className="px-2 py-0.5 rounded bg-rose-500 text-white text-[10px] font-black disabled:opacity-50 hover:brightness-110">
                        ✕ Close
                      </button>
                    ) : (
                      <span className="text-[9px] opacity-50">–</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'auto' && (
        <div className="rounded-xl bg-slate-950/60 border border-fuchsia-400/40 p-3 space-y-3" data-testid="admin-auto-settle-panel">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="text-sm font-black text-fuchsia-300">⚡ Auto-Settle Rules</div>
              <div className="text-[10px] opacity-70">Zero-touch: auto force-settle any ESCROW_LOCKED order older than N hours (skips disputed by default).</div>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="admin-auto-toggle-wrap">
              <input type="checkbox" checked={!!autoRules.enabled}
                     onChange={(e) => saveAutoRules({ enabled: e.target.checked })}
                     className="sr-only peer" data-testid="admin-auto-toggle" />
              <span className="w-11 h-6 rounded-full bg-slate-700 relative peer-checked:bg-fuchsia-500 transition
                                after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:transition
                                peer-checked:after:translate-x-5"></span>
              <span className="text-[11px] font-black">{autoRules.enabled ? 'ON' : 'OFF'}</span>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="text-[11px]">
              <span className="block opacity-70 mb-0.5">Age threshold (hours)</span>
              <input type="number" min="1" max="720" value={autoRules.hours}
                     onChange={(e) => setAutoRules({ ...autoRules, hours: parseInt(e.target.value || '48', 10) })}
                     onBlur={(e) => saveAutoRules({ hours: parseInt(e.target.value || '48', 10) })}
                     data-testid="admin-auto-hours"
                     className="w-full px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
            </label>
            <label className="text-[11px] flex items-center gap-2 mt-4">
              <input type="checkbox" checked={!!autoRules.skip_disputed}
                     onChange={(e) => saveAutoRules({ skip_disputed: e.target.checked })}
                     data-testid="admin-auto-skip-disputed" />
              <span>Skip disputed orders</span>
            </label>
            <div className="text-[10px] opacity-70 sm:mt-4">
              Runs every 30 min. Last run:<br />
              <b className="text-fuchsia-300">{autoRules.last_run ? new Date(autoRules.last_run).toLocaleString() : '—'}</b>
              {typeof autoRules.last_settled_count === 'number' && (
                <span className="ml-1">· settled {autoRules.last_settled_count}</span>
              )}
            </div>
          </div>

          {/* Wave 32 · Settle Simulator */}
          <SettleSimulator hours={autoRules.hours} skipDisputed={autoRules.skip_disputed} />

          {/* Wave 33 · Revenue Forecaster */}
          <RevenueForecaster skipDisputed={autoRules.skip_disputed} onPick={(h) => saveAutoRules({ hours: h })} />

          {/* Wave 33 · Auto-Settle Run History */}
          <AutoSettleHistory />

          <div className="text-[9px] opacity-60 flex items-center gap-1">
            💡 Tip: Simulate first, then flip ON. Watch <b>Live Signals</b> panel to see auto-settles fire.
          </div>
        </div>
      )}
    </div>
  );
}


// =============== Wave 33 · Revenue Forecaster ===============
function RevenueForecaster({ skipDisputed, onPick }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [spark, setSpark] = useState([]);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/admin/atc/auto-settle/forecast', { params: { skip_disputed: skipDisputed } }),
        api.get('/admin/atc/auto-settle/forecast-history', { params: { days: 7 } }),
      ]);
      setData(r.data);
      setSpark((s.data.series || []).map((d) => d.settled));
    } catch (e) { /* silent */ }
    finally { setBusy(false); }
  }, [skipDisputed]);
  useEffect(() => { load(); }, [load]);

  const maxPayout = data ? Math.max(1, ...data.points.map((p) => p.total_payout_at)) : 1;

  return (
    <div className="rounded-xl bg-slate-900 border border-cyan-400/30 p-3 mt-2" data-testid="admin-revenue-forecaster">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-[11px] font-black text-cyan-300">📈 Revenue Forecaster</span>
        <span className="text-[10px] opacity-70">Compare 24h / 48h / 72h / 7d thresholds</span>
        {spark.length > 0 && (
          <span className="ml-2 flex items-center gap-1 text-[10px] opacity-80" title="7-day auto-settled trend">
            📊
            <Sparkline data={spark} width={80} height={16} color="#22d3ee" />
          </span>
        )}
        <button onClick={load} disabled={busy}
                data-testid="admin-forecast-refresh"
                className="ml-auto text-[9px] px-2 py-0.5 rounded bg-slate-800 border border-slate-700 hover:bg-slate-700">
          {busy ? '⏳' : '↻'} refresh
        </button>
      </div>

      {data && (
        <>
          {/* Bar chart */}
          <div className="space-y-1.5" data-testid="admin-forecast-chart">
            {data.points.map((p) => {
              const wPct = Math.round((p.total_payout_at / maxPayout) * 100);
              const isBest = data.recommended && data.recommended.hours === p.hours && p.candidates > 0;
              return (
                <button key={p.hours} onClick={() => onPick(p.hours)}
                        title={`Set threshold to ${p.hours}h`}
                        data-testid={`admin-forecast-bar-${p.hours}`}
                        className="w-full text-left group">
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className={`w-12 font-black ${isBest ? 'text-emerald-300' : 'text-slate-300'}`}>
                      {p.hours}h {isBest && '👑'}
                    </span>
                    <div className="flex-1 relative h-5 rounded bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded transition-all ${isBest ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' : 'bg-gradient-to-r from-cyan-500/70 to-fuchsia-500/70'} group-hover:brightness-110`}
                           style={{ width: `${Math.max(wPct, 3)}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-[10px] font-black text-white text-shadow">
                        {p.candidates} orders · {p.total_payout_at} AT payout
                        {p.risk_pct > 0 && <span className="ml-2 opacity-80">⚠ {p.risk_pct}% two-stage</span>}
                      </span>
                    </div>
                    {spark.length > 0 && (
                      <Sparkline data={spark} width={44} height={16} color={isBest ? '#10b981' : '#94a3b8'} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {data.recommended && data.recommended.candidates > 0 ? (
            <div className="mt-2 text-[10px] p-2 rounded-lg bg-emerald-500/10 border border-emerald-400/30">
              👑 <b>Recommended:</b> <b className="text-emerald-300">{data.recommended.hours}h</b> —
              max <b>{data.recommended.total_payout_at} AT</b> payout with {data.recommended.risk_pct}% two-stage risk.
              <span className="opacity-70"> Click bar to apply.</span>
            </div>
          ) : (
            <div className="mt-2 text-[10px] p-2 rounded-lg bg-slate-800/50 border border-slate-700 opacity-80 text-center">
              No candidates at any threshold — safe to enable.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Sparkline moved to /components/Sparkline.js (Wave 36 · shared component)


// =============== Wave 33 · Auto-Settle Run History ===============
function AutoSettleHistory() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/atc/auto-settle/history', { params: { limit: 20 } });
      setData(r.data);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="rounded-xl bg-slate-900 border border-amber-400/30 p-3 mt-2" data-testid="admin-autosettle-history">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-[11px] font-black text-amber-300">📜 Auto-Settle History</span>
        {data && (
          <span className="text-[10px] opacity-70">
            {data.count} runs · settled <b className="text-emerald-300">{data.total_settled_in_window}</b> · released <b className="text-emerald-300">{data.total_at_released_in_window}</b> AT
          </span>
        )}
        <a href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/admin/atc/weekly-digest.html`}
           target="_blank" rel="noopener noreferrer"
           data-testid="admin-weekly-digest-btn"
           className="ml-auto px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-pink-500 text-white text-[10px] font-black hover:scale-105 transition shadow shadow-fuchsia-500/30">
          📊 Weekly Digest PDF
        </a>
        <button onClick={load} data-testid="admin-history-refresh" className="text-[9px] px-2 py-0.5 rounded bg-slate-800 border border-slate-700 hover:bg-slate-700">↻</button>
      </div>
      <div className="max-h-56 overflow-y-auto">
        {(!data || data.count === 0) && <div className="text-[11px] opacity-60 py-3 text-center">No runs yet — enable auto-settle to start logging.</div>}
        {data && data.count > 0 && (
          <table className="w-full text-[10px]">
            <thead className="text-[9px] uppercase text-slate-400">
              <tr>
                <th className="text-left py-1 px-2">Run At</th>
                <th className="text-center py-1 px-2">Threshold</th>
                <th className="text-right py-1 px-2">Scanned</th>
                <th className="text-right py-1 px-2">Settled</th>
                <th className="text-right py-1 px-2">AT Released</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((r) => (
                <tr key={r.id} className="border-t border-slate-800" data-testid={`admin-history-row-${r.id.slice(0,8)}`}>
                  <td className="py-1 px-2">{new Date(r.run_at).toLocaleString()}</td>
                  <td className="py-1 px-2 text-center">{r.hours_threshold}h</td>
                  <td className="py-1 px-2 text-right">{r.candidates_scanned}</td>
                  <td className="py-1 px-2 text-right font-black text-emerald-300">{r.settled_count}</td>
                  <td className="py-1 px-2 text-right font-black text-cyan-300">{r.total_at_released}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// =============== Wave 32 · Settle Simulator (zero-risk preview) ===============
function SettleSimulator({ hours, skipDisputed }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customHours, setCustomHours] = useState(hours || 48);
  useEffect(() => { setCustomHours(hours || 48); }, [hours]);

  const simulate = async (h = customHours, sd = skipDisputed) => {
    setBusy(true);
    try {
      const r = await api.get('/admin/atc/auto-settle/simulate', {
        params: { hours: h, skip_disputed: sd, sample: 6 },
      });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Simulate failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl bg-slate-900 border border-fuchsia-400/30 p-3 mt-2" data-testid="admin-settle-simulator">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-black text-fuchsia-300">🎯 Settle Simulator</span>
        <span className="text-[10px] opacity-70">What-if preview · nothing is mutated</span>
        <div className="ml-auto flex items-center gap-1.5">
          <label className="text-[10px] opacity-80 flex items-center gap-1">
            Hours:
            <input type="number" min="1" max="720" value={customHours}
                   onChange={(e) => setCustomHours(parseInt(e.target.value || '48', 10))}
                   data-testid="admin-simulate-hours"
                   className="w-14 px-1 py-0.5 rounded bg-slate-800 border border-slate-700 text-[10px]" />
          </label>
          <button onClick={() => simulate(customHours, skipDisputed)} disabled={busy}
                  data-testid="admin-simulate-run"
                  className="px-2.5 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white text-[10px] font-black disabled:opacity-50 hover:scale-105 transition">
            {busy ? '⏳ Simulating…' : '🎯 Preview Now'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-2 space-y-2" data-testid="admin-simulate-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2">
              <div className="text-[9px] opacity-70 uppercase">Candidates</div>
              <div className="text-lg font-black text-fuchsia-300">{preview.candidate_count}</div>
            </div>
            <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2">
              <div className="text-[9px] opacity-70 uppercase">Total AT</div>
              <div className="text-lg font-black text-emerald-300">{preview.total_amount_at}</div>
            </div>
            <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2">
              <div className="text-[9px] opacity-70 uppercase">Payout to sellers</div>
              <div className="text-lg font-black text-cyan-300">{preview.total_payout_at}</div>
            </div>
            <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2">
              <div className="text-[9px] opacity-70 uppercase">Commission</div>
              <div className="text-lg font-black text-amber-300">{preview.total_commission_at}</div>
            </div>
          </div>
          <div className="text-[10px] opacity-80 flex items-center gap-3 flex-wrap">
            {preview.oldest_candidate_hours && (
              <span>⏱ Oldest: <b>{preview.oldest_candidate_hours}h</b> old</span>
            )}
            {preview.two_stage_count > 0 && (
              <span>🧪 Two-stage escrows: <b>{preview.two_stage_count}</b></span>
            )}
            <span className="opacity-60">Cutoff: {new Date(preview.cutoff_iso).toLocaleString()}</span>
          </div>
          {preview.sample.length > 0 && (
            <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-2">
              <div className="text-[9px] opacity-70 uppercase font-black mb-1">Sample (first {preview.sample.length})</div>
              <div className="space-y-0.5">
                {preview.sample.map((o) => (
                  <div key={o.order_id} className="flex items-center gap-2 text-[10px]" data-testid={`admin-simulate-sample-${o.order_id}`}>
                    <span className="font-mono opacity-70">{String(o.order_id).slice(0, 8)}…</span>
                    <span>@{o.buyer_username} → @{o.seller_username}</span>
                    <span className="ml-auto text-emerald-300 font-black">{o.total_amount_at} AT</span>
                    {o.two_stage && <span className="px-1 rounded bg-purple-500/25 text-purple-200 text-[8px]">2-stage</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {preview.candidate_count === 0 && (
            <div className="text-[11px] opacity-80 text-center py-3">
              ✅ Zero candidates at {preview.hours}h — safe to enable, nothing would auto-settle right now.
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// =============== Wave 36 · Admin — Appointment Dispute Queue ===============
function AppointmentDisputesQueue() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [breached, setBreached] = useState(0);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/marketplace/appointment-disputes');
      setRows(r.data.disputes || []);
      setBreached(r.data.sla_breached_count || 0);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (a, outcome) => {
    const label = outcome === 'refund_buyer' ? `Refund ₹${a.amount_paid_at} AT to @${a.buyer_email || 'buyer'}` : `Release ₹${a.amount_paid_at} AT to ${a.shop_name}`;
    if (!window.confirm(`${label}?\nThis closes the dispute permanently.`)) return;
    setBusy(a.id);
    try {
      const admin_note = window.prompt('Admin note (optional):') || null;
      await api.post(`/admin/marketplace/appointment-dispute/${a.id}/resolve`, { outcome, admin_note });
      toast.success('✅ Dispute resolved');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resolve failed'); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-purple-400/40 p-3 mb-4" data-testid="admin-apt-disputes">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div>
          <div className="text-sm font-black text-purple-300">📩 Appointment Disputes</div>
          <div className="text-[10px] opacity-70">Buyer-reported shop no-shows · admin review</div>
        </div>
        <div className="flex items-center gap-1">
          {breached > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500 text-white font-black animate-pulse"
                  data-testid="admin-apt-sla-breached-badge">⏰ {breached} SLA breach</span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-200 font-black">{rows.length} open</span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] opacity-60 py-4 text-center">🎉 No appointment disputes pending</div>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} data-testid={`admin-apt-dispute-${a.id.slice(0,8)}`}
                 className={`rounded-lg p-2 bg-slate-800/60 border ${a.sla_breached ? 'border-rose-400 ring-1 ring-rose-500/40 animate-pulse' : 'border-purple-400/25'}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="text-[11px] flex-1 min-w-0">
                  <div className="font-black flex flex-wrap items-center gap-1">
                    🏬 {a.shop_name} · 📅 {a.date} · 💰 <span className="text-emerald-300">{a.amount_paid_at} AT paid</span>
                    {a.sla_breached && (
                      <span data-testid={`admin-apt-sla-${a.id.slice(0,8)}`}
                            className="ml-1 px-1.5 py-0.5 rounded bg-rose-500 text-white text-[9px] font-black">
                        ⏰ SLA BREACHED · {a.hours_open}h
                      </span>
                    )}
                    {!a.sla_breached && typeof a.hours_open === 'number' && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 text-[9px] font-black">
                        {a.hours_open}h old
                      </span>
                    )}
                  </div>
                  <div className="opacity-80 text-[10px]">Buyer: @{a.buyer_email || a.buyer_id?.slice(0,8)} · Opened: {a.dispute_opened_at ? new Date(a.dispute_opened_at).toLocaleString() : '—'}</div>
                  {a.dispute_note && <div className="italic opacity-75 text-[10px] mt-0.5">"{a.dispute_note}"</div>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => resolve(a, 'refund_buyer')} disabled={busy === a.id}
                          data-testid={`admin-apt-resolve-refund-${a.id.slice(0,8)}`}
                          className="px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black disabled:opacity-50 hover:brightness-110">
                    💰 Refund Buyer
                  </button>
                  <button onClick={() => resolve(a, 'release_to_shop')} disabled={busy === a.id}
                          data-testid={`admin-apt-resolve-release-${a.id.slice(0,8)}`}
                          className="px-2 py-1 rounded bg-cyan-500 text-slate-900 text-[10px] font-black disabled:opacity-50 hover:brightness-110">
                    🏬 Release to Shop
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// =============== Wave 37 · Admin — Dispute SLA Alerts Banner ===============
function SlaAlertsBanner() {
  const [alerts, setAlerts] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/marketplace/alerts/pending', { params: { kind: 'dispute_sla_breach' } });
      setAlerts(r.data.alerts || []);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const dismiss = async (id) => {
    try {
      await api.post(`/admin/marketplace/alerts/${id}/dismiss`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      toast.success('Alert dismissed');
    } catch (_) { toast.error('Dismiss failed'); }
  };

  if (!alerts.length) return null;

  return (
    <div className="rounded-2xl bg-gradient-to-r from-rose-900 via-rose-800 to-slate-900 border-2 border-rose-400/60 p-3 mb-4 shadow-lg"
         data-testid="admin-sla-alerts-banner">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-lg animate-pulse">⏰</span>
          <div>
            <div className="text-sm font-black text-rose-100">Dispute SLA Alerts</div>
            <div className="text-[10px] opacity-80">Appointments unresolved for &gt; 12 hours — action needed</div>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500 text-white font-black animate-pulse"
                data-testid="admin-sla-alerts-count">{alerts.length}</span>
        </div>
        <button onClick={() => setCollapsed((v) => !v)} data-testid="admin-sla-alerts-toggle"
                className="text-[10px] px-2 py-1 rounded-lg bg-rose-500/30 hover:bg-rose-500/50 text-rose-100 font-black">
          {collapsed ? '▼ Show' : '▲ Hide'}
        </button>
      </div>
      {!collapsed && (
        <div className="space-y-1.5">
          {alerts.map((a) => (
            <div key={a.id} data-testid={`admin-sla-alert-${a.id.slice(0,8)}`}
                 className="flex items-center justify-between gap-2 rounded-lg bg-slate-950/50 border border-rose-400/30 px-2 py-1.5">
              <div className="text-[11px] flex-1 min-w-0">
                <div className="font-black truncate">{a.message}</div>
                <div className="opacity-70 text-[10px]">
                  Opened: {a.opened_at ? new Date(a.opened_at).toLocaleString() : '—'} · Amount: {a.amount_at} AT
                </div>
              </div>
              <button onClick={() => dismiss(a.id)} data-testid={`admin-sla-dismiss-${a.id.slice(0,8)}`}
                      className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-[10px] font-black">
                ✓ Dismiss
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

